import { z } from 'zod';

/*
 * The encryption vault: what a new device needs to read old messages and be trusted (the key-backup key and the
 * cross-signing private keys), encrypted on a device with AES-256-GCM under a key derived from the user's backup
 * password (PBKDF2-SHA-256). The server only stores the envelope; it never sees the password or the keys.
 */

export const VAULT_PBKDF2_ITERATIONS = 600_000;
export const VAULT_PASSWORD_MIN = 8;

const b64 = (max: number) => z.string().min(1).max(max).regex(/^[A-Za-z0-9+/]+={0,2}$/);
export const vaultEnvelopeSchema = z.object({
  v: z.literal(1),
  kdf: z.literal('pbkdf2-sha256'),
  iterations: z.number().int().min(100_000).max(5_000_000),
  salt: b64(64),
  iv: b64(32),
  ciphertext: b64(12_000),
});
export type VaultEnvelope = z.infer<typeof vaultEnvelopeSchema>;

/** The vault's plaintext (JSON, inside the ciphertext). */
export interface VaultContents {
  /** BackupDecryptionKey, base64. */
  backupKey: string;
  crossSigning: { master: string; selfSigning: string; userSigning: string };
}

const enc = new TextEncoder();
const toB64 = (b: Uint8Array) => btoa(String.fromCharCode(...b));
const fromB64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

async function vaultKey(password: string, salt: Uint8Array<ArrayBuffer>, iterations: number) {
  const base = await crypto.subtle.importKey('raw', enc.encode(password.normalize('NFKC')), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey({ name: 'PBKDF2', hash: 'SHA-256', salt, iterations }, base, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}

/** Encrypts the vault contents under the backup password (Web Crypto: browsers and Node). */
export async function sealVault(contents: VaultContents, password: string, iterations = VAULT_PBKDF2_ITERATIONS): Promise<VaultEnvelope> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await vaultKey(password, salt, iterations);
  const data = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(JSON.stringify(contents))));
  return { v: 1, kdf: 'pbkdf2-sha256', iterations, salt: toB64(salt), iv: toB64(iv), ciphertext: toB64(data) };
}

/** Decrypts the vault; null when the password is wrong (the authentication tag does not match). */
export async function openVault(vault: VaultEnvelope, password: string): Promise<VaultContents | null> {
  const key = await vaultKey(password, fromB64(vault.salt), vault.iterations);
  try {
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: fromB64(vault.iv) }, key, fromB64(vault.ciphertext));
    return JSON.parse(new TextDecoder().decode(plain)) as VaultContents;
  } catch {
    return null;
  }
}
